import type React from 'react';
import {
	useIdleTimeout,
	type UseIdleTimeoutOptions,
} from '../../hooks/useIdleTimeout';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Modal } from '../overlays/Modal';

/**
 * PD-16414 — idle sign-out for a signed-in session: the clock
 * (`useIdleTimeout`) plus the warning shown before it runs out.
 *
 * Mount once, inside the signed-in part of the app. The CRM and the
 * storefront each pass their own limit, storage key and sign-out; with the
 * tenant's setting off this renders nothing and listens to nothing.
 *
 * `hold` pauses the clock while something other than input shows the user
 * is there; the CRM holds it for the length of a softphone call.
 *
 * The warning has one action, Stay signed in. Closing the dialog any other
 * way (Escape or any other key, the close button, a click outside it) does
 * the same. Moving the mouse, scrolling or touching the screen does NOT close
 * it: a hand on its way to the button would otherwise dismiss the dialog and
 * the click would land on the page behind it.
 */
export function IdleTimeoutGuard(
	props: UseIdleTimeoutOptions,
): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const { warningVisible, secondsRemaining, staySignedIn } =
		useIdleTimeout(props);

	return (
		<Modal
			isOpen={warningVisible}
			onClose={staySignedIn}
			title={t("You'll be signed out soon")}
			size="sm"
			actions={[
				{
					label: t('Stay signed in'),
					onClick: staySignedIn,
					variant: 'primary',
				},
			]}
		>
			{/* No aria-live: a region re-announced every second would drown
			    out everything else for a screen reader user. The dialog's
			    title is announced when it opens. */}
			<p className={cn('text-sm', colors.classes.textSecondary)}>
				{t(
					'For your security, you will be signed out in {{seconds}} seconds because there has been no activity.',
					{ seconds: secondsRemaining },
				)}
			</p>
		</Modal>
	);
}
